// Estado de las tareas que el paso 09 tiene idénticas en contado y en crédito: instalar la app (con
// el QR y el checklist que el silo verifica) y entregar la tarjeta física.
import { useEffect, useRef, useState } from 'react';
import { useAppInstallLink, useAppOnboarding, useMarkCardDelivered } from '../../api/useWizard';
import { cardDeliveryErrorMessage } from '../../cardDeliveryErrors';
import {
    appReached as clientReachedApp,
    onboardingSettled,
    SKIPPABLE_ONBOARDING_STEP,
} from './deliveryGate';
import type { AppOnboardingStepState, WizardSession } from '../../types';

/**
 * El escape vive en ESTA pestaña, no en el silo: la bandeja de Pendientes lleva el seguimiento real
 * de la tarjeta (`CARD_TO_DELIVER` sale de `accountStatus === 'OPEN' && !pcfProvisioned`).
 */
const skipKey = (sessionId: string): string => `wizard.cardSkipped.${sessionId}`;

/** El almacenamiento puede no existir o estar bloqueado: eso no puede tumbar el paso 09. */
function readSkipped(sessionId: string): boolean {
    try {
        return sessionStorage.getItem(skipKey(sessionId)) === '1';
    } catch {
        return false;
    }
}

function writeSkipped(sessionId: string, skipped: boolean): void {
    try {
        if (skipped) sessionStorage.setItem(skipKey(sessionId), '1');
        else sessionStorage.removeItem(skipKey(sessionId));
    } catch {
        /* sin almacenamiento el escape dura lo que dure este montaje del paso 09, como antes */
    }
}

export interface DeliveryTasksState {
    /** Enlace que el cliente escanea. `null` mientras no llega o si no se pudo generar. */
    installLink: string | null;
    linkError: unknown;
    linkFetching: boolean;
    retryLink: () => void;
    steps: AppOnboardingStepState[];
    onboardingError: unknown;
    retryOnboarding: () => void;
    retryingOnboarding: boolean;
    /** El cliente ya entró a su app: alguna condición se cumplió. */
    appReached: boolean;
    /** El registro del cliente ya no traba el paso. */
    onboardingSettled: boolean;
    /** La tarjeta ya está resuelta: entregada, saltada, o no hay ninguna que entregar. */
    cardResolved: boolean;
    /** La entrega ya quedó registrada (descontó inventario): lo publica el silo o se acaba de registrar. */
    cardDelivered: boolean;
    /** El cliente ya activó su tarjeta física en la app: eso cierra la tarea. */
    cardActivated: boolean;
    /** La tienda no tiene tarjetas: la tarea no se muestra ni traba el paso. */
    cardTaskHidden: boolean;
    /**
     * El escape de la tarjeta sigue haciendo falta. Cuelga de que la condición trabe el gate, NUNCA
     * de la entrega: el plástico entregado y sin activar deja el paso igual de trabado.
     */
    cardEscapeVisible: boolean;
    /** El comprador ya trae plástico vigente, así que no hay nada que entregarle. */
    cardAlreadyOwned: boolean;
    /** La tienda se quedó sin plásticos y el vendedor salió por el escape. Sobrevive a la recarga. */
    cardSkipped: boolean;
    /** La cuenta quedó pendiente de revisión: la tarjeta se va a la bandeja del vendedor. */
    cardDeferredByAccount: boolean;
    /** Reintenta registrar la entrega cuando el registro automático falló. */
    deliverCard: () => void;
    skipCard: () => void;
    /** Vuelve atrás el escape: es local y no tocó el inventario, así que se deshace sin costo. */
    undoSkipCard: () => void;
    delivering: boolean;
    deliveryError: string | null;
    /** Las dos tareas compartidas cumplidas. Cada pista le suma lo suyo. */
    settled: boolean;
}

/** @param enabled `false` para el comprador que ya traía cuenta: ni QR ni tarjeta le aplican. */
export function useDeliveryTasks(
    sessionId: string,
    session: WizardSession | undefined,
    enabled: boolean,
): DeliveryTasksState {
    const {
        data: appLink,
        error: linkError,
        isFetching: linkFetching,
        refetch: refetchLink,
    } = useAppInstallLink(sessionId, enabled);
    const {
        data: onboarding,
        error: onboardingError,
        refetch: refetchOnboarding,
    } = useAppOnboarding(sessionId, enabled);
    const markCard = useMarkCardDelivered(sessionId);
    const [cardSkipped, setCardSkipped] = useState(() => readSkipped(sessionId));

    // El reintentar sigue al CLIC, no al `isFetching`: atado a la query, el poll de 5 s lo pone en
    // carga solo, sin que nadie lo toque.
    const [retryingOnboarding, setRetryingOnboarding] = useState(false);
    const retryOnboarding = (): void => {
        setRetryingOnboarding(true);
        void refetchOnboarding().finally(() => {
            setRetryingOnboarding(false);
        });
    };

    const cardAlreadyOwned = session?.delivery.cardAlreadyOwned === true;
    const cardDeferredByAccount = session?.accountStatus === 'FAILED';
    const cardDelivered = session?.delivery.cardDelivered === true || markCard.isSuccess;
    const cardStepStatus = (onboarding?.steps ?? []).find(
        (s) => s.step === SKIPPABLE_ONBOARDING_STEP,
    )?.status;
    const cardActivated = cardStepStatus === 'COMPLETED';
    const cardStockAvailable = onboarding?.cardStockAvailable ?? null;
    const cardTaskHidden = cardStockAvailable === false;
    // Activada falta registrar la entrega (descuenta inventario); mientras viaje o falle, no cierra.
    const needsRegistration =
        cardActivated &&
        !cardTaskHidden &&
        !cardAlreadyOwned &&
        !cardDeferredByAccount &&
        !cardDelivered;
    // Una lectura nuestra caída de la activación no traba al vendedor; solo no descuenta nada.
    const cardResolved =
        cardTaskHidden ||
        cardDelivered ||
        (cardActivated && !needsRegistration) ||
        cardStepStatus === 'UNVERIFIABLE' ||
        cardSkipped ||
        cardAlreadyOwned ||
        cardDeferredByAccount;

    // La activación del cliente registra la entrega: descuenta una tarjeta de la tienda, una sola vez.
    // El silo la deduplica por venta (`pcfProvisioned`), así que una recarga tampoco descuenta dos.
    const deliveryRequested = useRef(false);
    const shouldRegisterDelivery = enabled && needsRegistration;
    useEffect(() => {
        if (!shouldRegisterDelivery || deliveryRequested.current) return;
        deliveryRequested.current = true;
        markCard.mutate();
    }, [shouldRegisterDelivery, markCard]);
    // Sin tarjetas en la tienda la activación no cuenta, igual que con el escape.
    const cardReleased = cardSkipped || cardTaskHidden;
    const settled = onboardingSettled(onboarding, cardReleased) && cardResolved;

    // La condición que traba el gate. Con la cuenta fallida el silo la manda `UNVERIFIABLE` y con
    // plástico previo, `COMPLETED`: en los dos casos no traba y el escape no tiene nada que soltar.
    const cardStepPending = (onboarding?.steps ?? []).some(
        (s) => s.step === SKIPPABLE_ONBOARDING_STEP && s.status === 'PENDING',
    );

    return {
        installLink: appLink?.shortUrl ?? null,
        linkError,
        linkFetching,
        retryLink: () => void refetchLink(),
        steps: onboarding?.steps ?? [],
        onboardingError,
        retryOnboarding,
        retryingOnboarding,
        appReached: clientReachedApp(onboarding?.steps ?? []),
        onboardingSettled: onboardingSettled(onboarding, cardReleased),
        cardResolved,
        cardDelivered,
        cardActivated,
        cardTaskHidden,
        // El escape solo hace falta si no sabemos si hay tarjetas: con inventario conocido sobra.
        // Sin inventario conocido también sirve de salida si el registro de la entrega falló.
        cardEscapeVisible:
            (cardStepPending || (needsRegistration && markCard.isError)) &&
            !cardSkipped &&
            cardStockAvailable === null,
        cardAlreadyOwned,
        cardSkipped,
        cardDeferredByAccount,
        deliverCard: () => {
            markCard.mutate();
        },
        skipCard: () => {
            writeSkipped(sessionId, true);
            setCardSkipped(true);
        },
        undoSkipCard: () => {
            writeSkipped(sessionId, false);
            setCardSkipped(false);
        },
        delivering: markCard.isPending,
        deliveryError: markCard.isError ? cardDeliveryErrorMessage(markCard.error) : null,
        settled,
    };
}
